import React, { useState, useEffect } from 'react'
import { STRIPE_TEXT_PRIMARY } from 'constants/stripe'
import { sharedStyles } from './shared-styles'

interface ProrationSettingsProps {
  onProrationChange?: (
    enabled: boolean,
    behavior: 'next_invoice' | 'immediately' | 'none'
  ) => void
  initialEnabled?: boolean
  initialBehavior?: 'next_invoice' | 'immediately' | 'none'
  hasChanges?: boolean
}

const ProrationSettings: React.FC<ProrationSettingsProps> = ({
  onProrationChange,
  initialEnabled = false,
  initialBehavior = 'none',
  hasChanges = false,
}) => {
  const [prorateChanges, setProrateChanges] = useState<boolean>(initialEnabled)
  const [prorationBehavior, setProrationBehavior] = useState<
    'next_invoice' | 'immediately' | 'none'
  >(initialBehavior)

  // Update state when props change
  useEffect(() => {
    setProrateChanges(initialEnabled)
    setProrationBehavior(initialBehavior)
  }, [initialEnabled, initialBehavior])

  const handleProrationToggle = (enabled: boolean) => {
    // Don't allow changes if there are no changes to subscription items
    if (!hasChanges) {
      return
    }
    setProrateChanges(enabled)
    onProrationChange?.(enabled, prorationBehavior)
  }

  const handleBehaviorChange = (
    behavior: 'next_invoice' | 'immediately' | 'none'
  ) => {
    // Don't allow changes if there are no changes to subscription items
    if (!hasChanges) {
      return
    }
    setProrationBehavior(behavior)
    onProrationChange?.(prorateChanges, behavior)
  }

  return (
    <div style={sharedStyles.sectionContainer}>
      <h6 style={sharedStyles.sectionHeading}>Proration behavior</h6>

      {/* Proration behavior toggle */}
      <div style={{ marginBottom: '16px' }}>
        <div style={sharedStyles.checkboxContainer}>
          <input
            type="checkbox"
            id="prorateChanges"
            checked={prorateChanges}
            onChange={(e) => handleProrationToggle(e.target.checked)}
            style={{
              ...sharedStyles.checkbox,
              opacity: !hasChanges ? 0.5 : 1,
              cursor: !hasChanges ? 'not-allowed' : 'pointer',
            }}
            disabled={!hasChanges}
          />
          <span
            style={{
              ...sharedStyles.checkboxLabel,
              color: !hasChanges ? '#6b7c93' : STRIPE_TEXT_PRIMARY,
              cursor: !hasChanges ? 'not-allowed' : 'pointer',
            }}
          >
            Prorate changes
          </span>
        </div>

        {/* Radio button options - only show when toggle is ON */}
        {prorateChanges && (
          <div style={sharedStyles.subSectionContainer}>
            <div style={{ marginBottom: '8px' }}>
              <label
                style={{
                  ...sharedStyles.radioLabel,
                  color: !hasChanges ? '#6b7c93' : STRIPE_TEXT_PRIMARY,
                  cursor: !hasChanges ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="prorationBehavior"
                  value="next_invoice"
                  checked={prorationBehavior === 'next_invoice'}
                  onChange={(e) =>
                    handleBehaviorChange(
                      e.target.value as 'next_invoice' | 'immediately'
                    )
                  }
                  style={{
                    ...sharedStyles.radioInput,
                    opacity: !hasChanges ? 0.5 : 1,
                    cursor: !hasChanges ? 'not-allowed' : 'pointer',
                  }}
                  disabled={!hasChanges}
                />
                Charge proration amount on next invoice
              </label>
            </div>
            <div>
              <label
                style={{
                  ...sharedStyles.radioLabel,
                  color: !hasChanges ? '#6b7c93' : STRIPE_TEXT_PRIMARY,
                  cursor: !hasChanges ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="prorationBehavior"
                  value="immediately"
                  checked={prorationBehavior === 'immediately'}
                  onChange={(e) =>
                    handleBehaviorChange(
                      e.target.value as 'next_invoice' | 'immediately'
                    )
                  }
                  style={{
                    ...sharedStyles.radioInput,
                    opacity: !hasChanges ? 0.5 : 1,
                    cursor: !hasChanges ? 'not-allowed' : 'pointer',
                  }}
                  disabled={!hasChanges}
                />
                Charge proration amount immediately
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProrationSettings
